import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import SuperAdminLayout from '../components/SuperAdminLayout';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement } from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';
import '../styles/superadmin.css';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement);

const BASE = 'https://menu.searchisend.in';

const PaymentHistory = () => {
    const navigate = useNavigate();
    const token = localStorage.getItem('saas_superadmin_token');
    
    const [payments, setPayments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    
    const [filters, setFilters] = useState({
        search: '',
        method: '',
        status: '',
        date_filter: 'all_time',
        custom_start: '',
        custom_end: ''
    });

    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 20;

    useEffect(() => {
        setCurrentPage(1);
    }, [filters]);

    // Debounce state for search input
    const [debouncedSearch, setDebouncedSearch] = useState(filters.search);

    useEffect(() => {
        if (!token) {
            navigate('/superadmin_v2_secure_login');
            return;
        }
    }, [token, navigate]);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(filters.search);
        }, 500);
        return () => clearTimeout(handler);
    }, [filters.search]);

    const fetchPaymentHistory = async (silent = false) => {
        if (!silent) {
            setLoading(true);
            setError('');
        }
        try {
            const queryParams = new URLSearchParams({
                search: debouncedSearch,
                method: filters.method,
                status: filters.status,
                date_filter: filters.date_filter,
                custom_start: filters.custom_start,
                custom_end: filters.custom_end
            }).toString();

            const res = await fetch(`${BASE}/apimenu/v2/v2_super_dashboard.php?action=fetch_payment_history&${queryParams}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            if (data.success) {
                setPayments(data.payments);
            } else {
                if (!silent) setError(data.message || 'Error fetching payment history');
            }
        } catch (err) {
            if (!silent) setError('Network error');
        } finally {
            if (!silent) setLoading(false);
        }
    };

    useEffect(() => {
        fetchPaymentHistory(false);

        const pollInterval = setInterval(() => {
            fetchPaymentHistory(true);
        }, 10000);

        return () => clearInterval(pollInterval);
        // eslint-disable-next-line
    }, [debouncedSearch, filters.method, filters.status, filters.date_filter, filters.custom_start, filters.custom_end]);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const clearFilters = () => {
        setFilters({ search: '', method: '', status: '', date_filter: 'all_time', custom_start: '', custom_end: '' });
    };

    const analytics = useMemo(() => {
        if (!payments || payments.length === 0) return null;

        let totalRevenue = 0;
        let successfulPayments = 0;
        let pendingPayments = 0;
        const planCounts = {};
        const methodCounts = {};
        const revenueByDate = {}; 

        payments.forEach(p => {
            const isPaid = p.payment_status === 'Paid';
            if (isPaid) {
                totalRevenue += Number(p.amount) || 0;
                successfulPayments++;
                
                const plan = p.plan_name || 'Unknown';
                planCounts[plan] = (planCounts[plan] || 0) + (Number(p.amount) || 0);

                const method = p.payment_method || 'Unknown';
                methodCounts[method] = (methodCounts[method] || 0) + 1;

                const dateObj = new Date(p.created_at);
                const dateStr = dateObj.toLocaleDateString('en-CA'); 
                revenueByDate[dateStr] = (revenueByDate[dateStr] || 0) + (Number(p.amount) || 0);
            } else {
                pendingPayments++;
            }
        });

        const arpu = successfulPayments > 0 ? (totalRevenue / successfulPayments) : 0;
        const successRate = successfulPayments + pendingPayments > 0 
            ? ((successfulPayments / (successfulPayments + pendingPayments)) * 100) 
            : 0;

        const sortedDates = Object.keys(revenueByDate).sort();
        const trendData = sortedDates.map(date => revenueByDate[date]);

        return {
            totalRevenue,
            successfulPayments,
            arpu,
            successRate,
            planCounts,
            methodCounts,
            trendLabels: sortedDates,
            trendData
        };
    }, [payments]);

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { position: 'bottom' },
        }
    };
    // Pagination logic
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentRecords = payments.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(payments.length / itemsPerPage);

    const handleNextPage = () => { if (currentPage < totalPages) setCurrentPage(prev => prev + 1); };
    const handlePrevPage = () => { if (currentPage > 1) setCurrentPage(prev => prev - 1); };

    return (
        <SuperAdminLayout>
                    <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
                        <div style={{ marginBottom: '2rem' }}>
                            <h2 className="sa-page-title">Payment History & Analytics</h2>
                            <p className="sa-page-subtitle">Track and manage all subscription payments across the platform with deep insights.</p>
                        </div>

                        {analytics && (
                            <div style={{ marginBottom: '2rem' }}>
                                {/* KPI Cards */}
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', marginBottom: '1.5rem' }}>
                                    <div className="sa-panel" style={{ padding: '1.5rem', borderLeft: '4px solid var(--success)', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                        <div style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', fontWeight: 600, textTransform: 'uppercase', marginBottom: '8px' }}>Total Revenue</div>
                                        <div style={{ color: 'var(--success)', fontSize: '2rem', fontWeight: 800 }}>₹{analytics.totalRevenue.toLocaleString()}</div>
                                    </div>
                                    <div className="sa-panel" style={{ padding: '1.5rem', borderLeft: '4px solid var(--accent-primary)', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                        <div style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', fontWeight: 600, textTransform: 'uppercase', marginBottom: '8px' }}>Successful Payments</div>
                                        <div style={{ color: 'var(--text-primary)', fontSize: '2rem', fontWeight: 800 }}>{analytics.successfulPayments.toLocaleString()}</div>
                                    </div>
                                    <div className="sa-panel" style={{ padding: '1.5rem', borderLeft: '4px solid var(--info)', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                        <div style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', fontWeight: 600, textTransform: 'uppercase', marginBottom: '8px' }}>Average Order Value</div>
                                        <div style={{ color: 'var(--text-primary)', fontSize: '2rem', fontWeight: 800 }}>₹{analytics.arpu.toFixed(2)}</div>
                                    </div>
                                    <div className="sa-panel" style={{ padding: '1.5rem', borderLeft: '4px solid var(--warning)', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                        <div style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', fontWeight: 600, textTransform: 'uppercase', marginBottom: '8px' }}>Payment Success Rate</div>
                                        <div style={{ color: analytics.successRate >= 90 ? 'var(--success)' : 'var(--warning)', fontSize: '2rem', fontWeight: 800 }}>
                                            {analytics.successRate.toFixed(1)}%
                                        </div>
                                    </div>
                                </div>

                                {/* Charts */}
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '15px' }}>
                                    <div className="sa-panel" style={{ padding: '1.5rem', height: '320px', display: 'flex', flexDirection: 'column' }}>
                                        <h3 style={{ margin: '0 0 1rem 0', fontSize: '1.1rem', color: 'var(--text-primary)' }}>Revenue Trend</h3>
                                        <div style={{ flex: 1, position: 'relative' }}>
                                            <Bar 
                                                data={{
                                                    labels: analytics.trendLabels.length > 0 ? analytics.trendLabels : ['No Data'],
                                                    datasets: [{
                                                        label: 'Revenue (₹)',
                                                        data: analytics.trendData.length > 0 ? analytics.trendData : [0],
                                                        backgroundColor: 'rgba(234, 88, 12, 0.8)',
                                                        borderRadius: 4
                                                    }]
                                                }} 
                                                options={{...chartOptions, plugins: { legend: { display: false } }}} 
                                            />
                                        </div>
                                    </div>
                                    
                                    <div className="sa-panel" style={{ padding: '1.5rem', height: '320px', display: 'flex', flexDirection: 'column' }}>
                                        <h3 style={{ margin: '0 0 1rem 0', fontSize: '1.1rem', color: 'var(--text-primary)' }}>Revenue by Plan</h3>
                                        <div style={{ flex: 1, position: 'relative', display: 'flex', justifyContent: 'center' }}>
                                            <div style={{ width: '100%', maxWidth: '250px' }}>
                                                <Doughnut 
                                                    data={{
                                                        labels: Object.keys(analytics.planCounts).length > 0 ? Object.keys(analytics.planCounts) : ['No Data'],
                                                        datasets: [{
                                                            data: Object.keys(analytics.planCounts).length > 0 ? Object.values(analytics.planCounts) : [1],
                                                            backgroundColor: ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899', '#64748b'],
                                                            borderWidth: 0
                                                        }]
                                                    }} 
                                                    options={chartOptions} 
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {error && <div style={{ background: 'var(--danger-bg)', color: 'var(--danger)', padding: '12px 16px', borderRadius: 'var(--radius-sm)', marginBottom: '20px', border: '1px solid var(--danger)', fontWeight: 500 }}><i className="fas fa-exclamation-circle"></i> {error}</div>}

                        <div className="sa-panel" style={{ padding: '1.5rem', marginBottom: '2rem' }}>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px', alignItems: 'center' }}>
                                <div style={{ flex: '1 1 300px', position: 'relative' }}>
                                    <i className="fas fa-search" style={{ position: 'absolute', left: '15px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-secondary)' }}></i>
                                    <input 
                                        type="text" 
                                        name="search" 
                                        placeholder="Search by Restaurant Name..." 
                                        className="sa-input" 
                                        style={{ width: '100%', paddingLeft: '45px', background: 'white' }}
                                        value={filters.search} 
                                        onChange={handleFilterChange} 
                                    />
                                </div>
                                <select name="method" className="sa-select" value={filters.method} onChange={handleFilterChange} style={{ flex: '1 1 150px', background: 'white' }}>
                                    <option value="">All Methods</option>
                                    <option value="Cashfree">Cashfree</option>
                                    <option value="Manual">Manual</option>
                                </select>
                                <select name="status" className="sa-select" value={filters.status} onChange={handleFilterChange} style={{ flex: '1 1 150px', background: 'white' }}>
                                    <option value="">All Statuses</option>
                                    <option value="Paid">Paid</option>
                                    <option value="Pending">Pending</option>
                                </select>
                                <select name="date_filter" className="sa-select" value={filters.date_filter} onChange={handleFilterChange} style={{ flex: '1 1 150px', background: 'white' }}>
                                    <option value="all_time">All Time</option>
                                    <option value="last_7_days">Last 7 Days</option>
                                    <option value="last_30_days">Last 30 Days</option>
                                    <option value="last_90_days">Last 90 Days</option>
                                    <option value="custom">Custom Range</option>
                                </select>
                                {filters.date_filter === 'custom' && (
                                    <>
                                        <input type="date" name="custom_start" className="sa-input" value={filters.custom_start} onChange={handleFilterChange} style={{ flex: '1 1 140px', background: 'white' }} title="Start Date" />
                                        <input type="date" name="custom_end" className="sa-input" value={filters.custom_end} onChange={handleFilterChange} style={{ flex: '1 1 140px', background: 'white' }} title="End Date" />
                                    </>
                                )}
                                <button onClick={clearFilters} className="sa-secondary-btn" style={{ padding: '0 20px', height: '42px' }}>
                                    <i className="fas fa-times"></i> Clear Filters
                                </button>
                            </div>
                        </div>

                        <div className="sa-panel" style={{ padding: 0, overflow: 'hidden' }}>
                            <div className="sa-table-container" style={{ margin: 0, border: 'none', borderRadius: 0, boxShadow: 'none' }}>
                                <table className="sa-table" style={{ margin: 0 }}>
                                    <thead>
                                        <tr>
                                            <th>Payment ID</th>
                                            <th>Restaurant Name</th>
                                            <th>Plan Details</th>
                                            <th>Amount</th>
                                            <th>Method</th>
                                            <th>Status</th>
                                            <th style={{ textAlign: 'right' }}>Date</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {loading ? (
                                            <tr><td colSpan="7" style={{ textAlign: 'center', padding: '4rem', color: 'var(--text-secondary)' }}><i className="fas fa-circle-notch fa-spin fa-2x" style={{ color: 'var(--accent-primary)', marginBottom: '10px' }}></i><br/>Loading payments...</td></tr>
                                        ) : currentRecords.length === 0 ? (
                                            <tr><td colSpan="7" style={{ textAlign: 'center', padding: '4rem', color: 'var(--text-secondary)' }}><i className="fas fa-receipt fa-3x" style={{ color: 'var(--border-color)', marginBottom: '1rem' }}></i><p>No payment records found matching your criteria.</p></td></tr>
                                        ) : currentRecords.map((payment, idx) => (
                                            <tr key={idx}>
                                                <td style={{ color: 'var(--text-secondary)', fontFamily: 'monospace' }}>{payment.payment_id}</td>
                                                <td><strong style={{ color: 'var(--text-primary)' }}>{payment.restaurant_name}</strong></td>
                                                <td><span style={{ background: 'var(--bg-dark)', padding: '4px 10px', borderRadius: '12px', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>{payment.plan_name}</span></td>
                                                <td><strong style={{ color: 'var(--text-primary)' }}>₹{Number(payment.amount).toFixed(2)}</strong></td>
                                                <td>
                                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                                                        <i className={payment.payment_method === 'Cashfree' ? 'fas fa-credit-card' : 'fas fa-money-bill-wave'} style={{ color: 'var(--accent-primary)' }}></i>
                                                        {payment.payment_method}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span style={{ 
                                                        display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '4px 10px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase',
                                                        background: payment.payment_status === 'Paid' ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                                                        color: payment.payment_status === 'Paid' ? 'var(--success)' : 'var(--danger)'
                                                    }}>
                                                        <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: payment.payment_status === 'Paid' ? 'var(--success)' : 'var(--danger)' }}></span>
                                                        {payment.payment_status}
                                                    </span>
                                                </td>
                                                <td style={{ textAlign: 'right', color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                                                    {new Date(payment.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true })}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {totalPages > 1 && (
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '1.5rem', background: 'white', padding: '1rem 1.5rem', borderRadius: '12px', border: '1px solid var(--border-color)', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
                                <div style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', fontWeight: 500 }}>
                                    Showing <strong style={{ color: 'var(--text-primary)' }}>{indexOfFirstItem + 1}</strong> to <strong style={{ color: 'var(--text-primary)' }}>{Math.min(indexOfLastItem, payments.length)}</strong> of <strong style={{ color: 'var(--text-primary)' }}>{payments.length}</strong> payments
                                </div>
                                <div style={{ display: 'flex', gap: '10px' }}>
                                    <button onClick={handlePrevPage} disabled={currentPage === 1} className="sa-secondary-btn" style={{ padding: '8px 16px', opacity: currentPage === 1 ? 0.5 : 1 }}>
                                        <i className="fas fa-chevron-left"></i> Prev
                                    </button>
                                    <div style={{ display: 'flex', alignItems: 'center', fontWeight: 600, padding: '0 10px', color: 'var(--text-primary)' }}>
                                        Page {currentPage} of {totalPages}
                                    </div>
                                    <button onClick={handleNextPage} disabled={currentPage === totalPages} className="sa-secondary-btn" style={{ padding: '8px 16px', opacity: currentPage === totalPages ? 0.5 : 1 }}>
                                        Next <i className="fas fa-chevron-right"></i>
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
        </SuperAdminLayout>
    );
};

export default PaymentHistory;

